import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, type AdminUser } from '../lib/api';
import { credits, relativeTime } from '../lib/format';

/**
 * People, and what an operator can do about them.
 *
 * Deliberately absent: deleting an account. A user who has traded is referenced
 * by the ledger, and removing them would leave entries pointing at nobody.
 * Suspension is reversible and recorded; for a GDPR erasure request the answer
 * is anonymisation, which keeps the financial history and drops the identity.
 */
export function Users() {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const users = useQuery({ queryKey: ['admin-users', query], queryFn: () => api.adminUsers(query || undefined) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin-users'] });

  return (
    <div className="stack">
      <div className="card">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="uq">Find someone</label>
          <input id="uq" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="email or name" />
          <div className="hint">{users.data?.length ?? 0} shown.</div>
        </div>
      </div>

      {users.isLoading && <div className="empty-state">Loading…</div>}
      {users.data?.length === 0 && <div className="empty-state">Nobody matches that.</div>}
      {users.data?.map((u) => <UserRow key={u.id} user={u} onChanged={refresh} />)}
    </div>
  );
}

function UserRow({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const act = (fn: () => Promise<unknown>, ok: string) => async () => {
    setError(null);
    setDone(null);
    try {
      await fn();
      setDone(ok);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.');
    }
  };

  // Entered in credits, stored in minor units. Parsed as a decimal string
  // rather than a float so 12.34 never becomes 12.339999999999999.
  const parsed = (() => {
    const m = amount.trim().match(/^(-?)(\d+)(?:[.,](\d{1,2}))?$/);
    if (!m) return null;
    const [, sign, whole, frac = '0'] = m;
    const units = BigInt(whole!) * 100n + BigInt(frac.padEnd(2, '0'));
    return sign === '-' ? -units : units;
  })();

  const adjust = useMutation({
    mutationFn: () => api.adjustCredits(user.id, parsed!, reason.trim()),
    onSuccess: () => {
      setAmount('');
      setReason('');
      setDone('Balance adjusted.');
      onChanged();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not adjust the balance.'),
  });

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        {user.suspended_at && <span className="badge closed">suspended</span>}
        {user.is_admin && <span className="badge open">admin</span>}
        <b style={{ fontSize: 15, flex: 1, minWidth: 200 }}>{user.email}</b>
        <span className="num" style={{ fontFamily: 'var(--mono)', fontSize: 14 }}>
          {credits(BigInt(user.cash))}
          {BigInt(user.escrow) > 0n && (
            <span className="muted"> · {credits(BigInt(user.escrow))} held</span>
          )}
        </span>
        <button style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => setOpen(!open)}>
          {open ? 'Close' : 'Manage'}
        </button>
      </div>

      <div className="meta" style={{ marginTop: 8, marginBottom: 0, fontSize: 12.5 }}>
        <span>joined {relativeTime(user.created_at)}</span>
        <span>{user.trades} trades</span>
        <span>{user.open_positions} positions</span>
        <span>{user.open_orders} open orders</span>
        {user.last_seen && <span>seen {relativeTime(user.last_seen)}</span>}
      </div>

      {user.suspension_reason && (
        <div className="notice error" style={{ marginTop: 10 }}>Suspended: {user.suspension_reason}</div>
      )}

      {open && (
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
          <div className="field">
            <label htmlFor={`amt-${user.id}`}>Adjust balance</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id={`amt-${user.id}`} value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder="100.00 or -25.00" style={{ maxWidth: 160 }} />
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Reason — recorded permanently" />
            </div>
            <div className="hint">
              Posted to the ledger as a normal double-entry transaction, never as an edit to a
              balance. A negative amount is a compensating entry, and cannot overdraw the account.
            </div>
          </div>
          <button
            disabled={parsed === null || parsed === 0n || reason.trim().length < 3 || adjust.isPending}
            onClick={() => adjust.mutate()}
          >
            {adjust.isPending ? 'Posting…' : parsed !== null && parsed < 0n ? 'Deduct' : 'Grant'}
          </button>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
            {user.suspended_at ? (
              <button onClick={act(() => api.setSuspended(user.id, false), 'Reinstated.')}>
                Reinstate
              </button>
            ) : (
              <button onClick={act(() => api.setSuspended(user.id, true, 'suspended by an administrator'), 'Suspended.')}>
                Suspend
              </button>
            )}
            <button onClick={act(() => api.setAdmin(user.id, !user.is_admin), 'Role changed.')}>
              {user.is_admin ? 'Remove admin' : 'Make admin'}
            </button>
            <button onClick={act(() => api.revokeSessions(user.id), 'Signed out everywhere.')}>
              Sign out everywhere
            </button>
          </div>
          <p className="hint" style={{ marginTop: 10 }}>
            Suspending also ends their sessions immediately — otherwise a suspended account keeps
            acting until its token expires. Their positions and orders are left untouched.
          </p>

          {error && <div className="notice error">{error}</div>}
          {done && <div className="notice ok">{done}</div>}
        </div>
      )}
    </div>
  );
}
